import React, { useState, useEffect, useCallback } from 'react';
import './PortfolioReturns.scss';
import PortfolioReturnsToolbar from './toolbar/PortfolioReturnsToolbar';
import { PortfolioReturnsChart } from './chart/PortfolioReturnsChart';
import {
    buildPortfolioReturnsData,
    initPortfolioReturnsData,
} from './chart/portfolioReturnsData';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { PortfolioReturnsData } from './chart/portfolioReturnsData';
import { calculateTimeDomain } from 'utils/components/TimeSeriesChart/timeSeriesTimeScale';
import { XAxisContextProvider } from 'utils/components/TimeSeriesChart/XAxisContext';
import PortfolioReturnsLegends from './legends/PortfolioReturnsLegends';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { usePortfolioReturnsComponentSlice } from 'widgets/portfolioReturns/store/component/componentSlice';
import { usePortfolioReturnsConfigSlice } from 'widgets/portfolioReturns/store/configuration/configSlice';
import { usePortfolioReturnsStreamData } from 'widgets/portfolioReturns/store/stream/streamSlice';
import { useWidgetContext } from 'widgets/WidgetContext';
import { usePortfolioReturnsDimensions } from 'widgets/portfolioReturns/dimensions';
import WidgetToolbarShowHidePanel from 'widgets/WidgetToolbarShowHidePanel/WidgetToolbarShowHidePanel';
import { usePortfolioReturnsLegendStatus } from 'widgets/portfolioReturns/legends/legendStatus';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks';
import { ZoomControl } from 'utils/components/Zoom/chartZoom';
import { PortfolioReturnsTitlePanel } from 'widgets/portfolioReturns/title/PortfolioReturnsTitlePanel';
import classNames from 'classnames';

export default function PortfolioReturns() {
    const { width, height, widgetKey } = useWidgetContext();

    useWidgetStream();

    const { component, dispatchComponentChanged } =
        usePortfolioReturnsComponentSlice();

    const { config, dispatchConfigChanged } = usePortfolioReturnsConfigSlice();

    const { days, invertSellLine, breaks } = config;

    const displayingMoreThanOneDay = !!(days && days > 1);

    const { combineSellsAndShorts, showBuysAsPositive } =
        useSelectAppSettings();

    const stream = usePortfolioReturnsStreamData();

    const [data, setData] = useState<PortfolioReturnsData>(
        initPortfolioReturnsData
    );
    useEffect(() => {
        setData(
            buildPortfolioReturnsData(
                component,
                showBuysAsPositive,
                stream,
                !!invertSellLine
            )
        );
    }, [component, stream, showBuysAsPositive, invertSellLine]);

    const {
        availableBasisDays,
        breaksInTrading,
        timeSeries,
        rangeLabels,
        timeLineLabels,
        multipleSides,
    } = data;

    const timeDomain = calculateTimeDomain(timeSeries);
    timeDomain[1] = timeDomain[1] + 60000 * 2; // add 2 mins to the end

    const onZoomOutAtDefaultZoom = useCallback(() => {
        dispatchConfigChanged({ days: (days ?? 1) * 2 });
    }, [days, dispatchConfigChanged]);

    const onZoomIn = useCallback(
        ({ resetZoom }: ZoomControl) => {
            if (days && days > 1) {
                dispatchConfigChanged({ days: Math.floor(days / 2) });
                resetZoom();
            }
        },
        [days, dispatchConfigChanged]
    );

    const zoomLabel = config.days
        ? `${config.days} day${config.days > 1 ? 's' : ''}`
        : '1 day';
    const timeLabels = config.showMarketSessionTimes ? timeLineLabels : [];

    const { chartHeight, headerHeight } = usePortfolioReturnsDimensions();
    const { allHidden: allLegendsHidden } = usePortfolioReturnsLegendStatus();

    const { hasValidSelection } = useWidgetPortfolioSelectionStatus();

    const headerStyle = { height: headerHeight };

    const cssClasses = classNames(
        'PortfolioReturns',
        data.valuesSize,
        breaks === 'line' ? 'breaks-as-lines' : undefined,
        breaks === 'hide' ? 'breaks-hidden' : undefined
    );

    return (
        <div className={cssClasses} data-widget-id={widgetKey}>
            <div className="PortfolioReturnsContainer">
                <XAxisContextProvider
                    timeSeriesChartWidth={width}
                    startTime={timeDomain[0]}
                    endTime={timeDomain[1]}
                    breaks={breaksInTrading}
                    breaksType={breaks}
                    rangeLabels={rangeLabels}
                    timeLineLabels={timeLabels}
                    onZoomIn={onZoomIn}
                    onZoomScaleBoundary={onZoomOutAtDefaultZoom}
                    resetZoomOnTimeRangeChange={false}
                    showTimeLineDate={displayingMoreThanOneDay}
                >
                    <WidgetToolbarShowHidePanel
                        mustShow={!hasValidSelection || !allLegendsHidden}
                    >
                        <div className="widget-header" style={headerStyle}>
                            {hasValidSelection && (
                                <div>
                                    <PortfolioReturnsLegends
                                        combineSellsAndShorts={
                                            combineSellsAndShorts
                                        }
                                        config={config}
                                        componentState={component}
                                        data={data}
                                        width={width}
                                    />
                                </div>
                            )}

                            <PortfolioReturnsToolbar
                                availableBasisDays={availableBasisDays}
                                component={component}
                                config={config}
                                widgetHeight={height}
                                zoomLabel={zoomLabel}
                                onComponentChange={dispatchComponentChanged}
                                onConfigChange={dispatchConfigChanged}
                            />
                        </div>
                    </WidgetToolbarShowHidePanel>
                    <PortfolioReturnsTitlePanel />
                    {hasValidSelection && (
                        <PortfolioReturnsChart
                            combineSellsAndShorts={combineSellsAndShorts}
                            config={config}
                            componentState={component}
                            timeSeries={timeSeries}
                            multipleSides={multipleSides}
                            width={width}
                            height={chartHeight}
                        />
                    )}
                </XAxisContextProvider>
            </div>
        </div>
    );
}
